/**
 * PATH: packages/pricelist/src/industryTemplates.ts
 * FILE_ID: PKG-PRICELIST-TEMPLATES-001
 * PURPOSE: Data-driven industry starter templates for the universal pricelist.
 *          Each entry is JUST a column_schema preset (snake_case keys, valid
 *          data_types) — "Steel" and "Waterproofing" differ only in this data,
 *          never in code. A company picks one (or many) when adding a section
 *          and customises freely afterwards. One company can run 5 industries
 *          side-by-side as 5 sections in the same pricelist.
 * WHY: The engine is universal (PriceBookSection.column_schema JSONB). Templates
 *      give each trade its "important columns" out of the box without bespoke
 *      code per industry.
 * FLOW: pick template → createSection({ name, column_schema: template.columns })
 * RELATED: types.ts (ColumnDef), SectionTemplatePicker, usePricelistWorkbook
 *
 * NOTE: keys must be snake_case and must NOT collide with core fields
 *       (item_code, item_name, description, unit, base_cost, markup_percent…).
 */
import type { ColumnDef } from './types'

export interface IndustryTemplate {
  /** Stable id (also used as the default section code). */
  key: string
  /** Display name shown in the picker and used as the default section name. */
  label: string
  /** One-line description of the trade / what the columns capture. */
  description: string
  /** Default unit pre-filled on new rows in this section. */
  default_unit?: string
  /** Extra Excel columns layered on top of the core (code/desc/cost/markup/sell). */
  columns: ColumnDef[]
}

const col = (
  key: string,
  label: string,
  data_type: ColumnDef['data_type'] = 'text',
  extra: Partial<ColumnDef> = {},
): ColumnDef => ({ key, label, data_type, is_searchable: true, ...extra })

/**
 * Starter library. This is intentionally plain data — extend it by adding
 * entries here (or, later, by loading org-saved templates from the backend).
 */
export const INDUSTRY_TEMPLATES: IndustryTemplate[] = [
  {
    key: 'general',
    label: 'General construction',
    description: 'Labour + materials baseline — category and supplier columns.',
    default_unit: 'each',
    columns: [
      col('category', 'Category', 'enum', {
        enum_values: ['Labour', 'Material', 'Equipment', 'Subcontract'],
      }),
      col('supplier', 'Supplier'),
      col('lead_time_days', 'Lead time (days)', 'number'),
    ],
  },
  {
    key: 'steel',
    label: 'Steel fabrication / structural',
    description: 'Profiles, length, kg/m with auto total weight and finish.',
    default_unit: 'm',
    columns: [
      col('profile', 'Profile'),
      col('grade', 'Grade'),
      col('length_m', 'Length (m)', 'number', { unit: 'm' }),
      col('kg_per_m', 'kg/m', 'number', { unit: 'kg/m' }),
      col('total_kg', 'Total kg', 'number', { unit: 'kg', computed_from: '=length_m*kg_per_m' }),
      col('finish', 'Finish', 'enum', {
        enum_values: ['Black', 'Galvanised', 'Duplex', 'Painted'],
      }),
    ],
  },
  {
    key: 'waterproofing',
    label: 'Waterproofing',
    description: 'Membrane system, area, coats and coverage.',
    default_unit: 'm2',
    columns: [
      col('system', 'System', 'enum', {
        enum_values: ['Sheet membrane', 'Liquid membrane', 'Bituminous', 'Polyurethane'],
      }),
      col('area_m2', 'Area (m²)', 'number', { unit: 'm2' }),
      col('coats', 'Coats', 'number'),
      col('coverage_m2', 'Coverage (m²/unit)', 'number', { unit: 'm2' }),
      col('primer_required', 'Primer required', 'bool'),
    ],
  },
  {
    key: 'electrical',
    label: 'Electrical',
    description: 'Cable type, size, rating, phases and run length.',
    default_unit: 'm',
    columns: [
      col('cable_type', 'Cable type'),
      col('size_mm2', 'Size (mm²)', 'number', { unit: 'mm2' }),
      col('rating_amps', 'Rating (A)', 'number', { unit: 'A' }),
      col('phases', 'Phases', 'enum', { enum_values: ['Single', 'Three'] }),
      col('length_m', 'Run length (m)', 'number', { unit: 'm' }),
    ],
  },
  {
    key: 'plumbing',
    label: 'Plumbing / hydraulic',
    description: 'Pipe material, diameter, length and fittings.',
    default_unit: 'm',
    columns: [
      col('pipe_material', 'Pipe material', 'enum', {
        enum_values: ['Copper', 'PEX', 'PVC', 'Stainless', 'HDPE'],
      }),
      col('diameter_mm', 'Diameter (mm)', 'number', { unit: 'mm' }),
      col('length_m', 'Length (m)', 'number', { unit: 'm' }),
      col('fittings', 'Fittings', 'number'),
      col('pressure_rating', 'Pressure rating'),
    ],
  },
  {
    key: 'hvac',
    label: 'HVAC / mechanical',
    description: 'Equipment, capacity, airflow and duct sizing.',
    default_unit: 'each',
    columns: [
      col('equipment_type', 'Equipment type'),
      col('capacity_kw', 'Capacity (kW)', 'number', { unit: 'kW' }),
      col('airflow_ls', 'Airflow (L/s)', 'number', { unit: 'L/s' }),
      col('duct_size', 'Duct size'),
      col('refrigerant', 'Refrigerant'),
    ],
  },
  {
    key: 'concrete',
    label: 'Concrete / formwork',
    description: 'Mix grade, volume, reinforcement and placement.',
    default_unit: 'm3',
    columns: [
      col('mix_grade', 'Mix grade', 'enum', {
        enum_values: ['N20', 'N25', 'N32', 'N40', 'S50'],
      }),
      col('volume_m3', 'Volume (m³)', 'number', { unit: 'm3' }),
      col('reinforcement', 'Reinforcement'),
      col('slump_mm', 'Slump (mm)', 'number', { unit: 'mm' }),
      col('pump_required', 'Pump required', 'bool'),
    ],
  },
  {
    key: 'carpentry',
    label: 'Carpentry / joinery',
    description: 'Timber type, dimensions, length, grade and fixings.',
    default_unit: 'm',
    columns: [
      col('timber_type', 'Timber type'),
      col('dimensions', 'Dimensions'),
      col('length_m', 'Length (m)', 'number', { unit: 'm' }),
      col('grade', 'Grade', 'enum', {
        enum_values: ['Structural', 'Appearance', 'Treated', 'MGP10', 'MGP12'],
      }),
      col('fixings', 'Fixings'),
    ],
  },
  {
    key: 'civil',
    label: 'Civil / earthworks',
    description: 'Material, volume/area, compaction and haul distance.',
    default_unit: 'm3',
    columns: [
      col('material', 'Material'),
      col('volume_m3', 'Volume (m³)', 'number', { unit: 'm3' }),
      col('area_m2', 'Area (m²)', 'number', { unit: 'm2' }),
      col('compaction', 'Compaction'),
      col('haul_distance_km', 'Haul distance (km)', 'number', { unit: 'km' }),
    ],
  },
]

/** Look up a template by key (undefined for the "blank" choice). */
export function getIndustryTemplate(key: string | null | undefined): IndustryTemplate | undefined {
  if (!key) return undefined
  return INDUSTRY_TEMPLATES.find((t) => t.key === key)
}
